/**
 * What a gate says, read off the act (ADR-0044, plan Task 6).
 *
 * Every gate a Job mints carries `{job, act, evidence, target}` in its payload
 * and `job_id` + `supervisor_user_id` on its row
 * (`workers/jobAuthority.performAct`). This module is the one place that turns
 * that block into words, so the case file, the Work row, the tray, and the
 * Slack message cannot disagree about what a person is being asked.
 *
 * **The act is the source, never the gate kind.** Before this, the case file
 * switched on `WorkItemGate['kind']` and a kind it did not know fell to
 * `default` and rendered a card with no verbs. `job_act` (migration 0244) is
 * exactly such a kind, and it is the one every Wright pull request mints at
 * Supervised, so the switch made every draft pull request unanswerable. Keying
 * the copy off the act means a new act is a row in `JOB_ACT_COPY` and a new
 * kind is nothing at all.
 *
 * Copy rules apply to every string here: no em-dashes, a Job is always
 * possessed in copy ("Bailey's proposal"), never "job" for a ticket, never
 * "Worker" and never "container".
 */
import { DEFAULT_JOB_INSTANCE, JOB_ACT_IDS, } from "./jobs.js";
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function asString(value) {
    return typeof value === "string" && value.trim() ? value : null;
}
function isJobActId(value) {
    return typeof value === "string" && JOB_ACT_IDS.includes(value);
}
/**
 * Read the block off a gate payload. Returns null when the gate carries no
 * act, which is every gate minted before the cutover and every gate a legacy
 * path still opens: those keep the copy they always had.
 */
export function readJobGatePayload(payload) {
    if (!payload)
        return null;
    const act = isJobActId(payload.act) ? payload.act : null;
    const jobRaw = asObject(payload.job);
    if (!act && !jobRaw)
        return null;
    const job = jobRaw && asString(jobRaw.id) && asString(jobRaw.kind)
        ? {
            id: asString(jobRaw.id),
            kind: asString(jobRaw.kind),
            instance: asString(jobRaw.instance) ?? "default",
            personaSlug: asString(jobRaw.personaSlug) ?? "",
            agentName: asString(jobRaw.agentName),
            sentence: asString(jobRaw.sentence),
        }
        : null;
    return {
        job,
        act,
        evidence: asObject(payload.evidence) ?? {},
        target: payload.target === "person" ? "person" : "source",
        cappedByPerItemCap: payload.cappedByPerItemCap === true,
        heldReason: asString(payload.heldReason),
    };
}
/**
 * The extra verbs a CAPPED act's gate offers, as they travel on the wire.
 *
 * Two verbs are enough for an act that has one consequence. An act that ran
 * out of rounds has three real endings and no way to express them as yes or
 * no: grant one more, finish it yourself, or stop. So the click carries which
 * ending was chosen, and the dispatcher does that one thing.
 *
 * `continue` is the default and is what an uncapped gate's primary verb means,
 * so a click that names no choice is a `continue`.
 */
export const JOB_ACT_CHOICE_IDS = ["continue", "take_over", "stop"];
export function isJobActChoiceId(value) {
    return typeof value === "string" && JOB_ACT_CHOICE_IDS.includes(value);
}
export function isJobActVariantKind(value) {
    return value === "patch_ready" || value === "round_stopped" || value === "pr_needs_person";
}
/**
 * What the card says when nothing here can land the act and it has no other
 * home named. Deliberately says "yet": the dispatchers are Task 8b's, not a
 * permanent absence.
 */
export const JOB_ACT_NOT_ANSWERABLE_HERE = "This cannot be answered here yet. Its Agent will act on it the next time the line runs at autonomous.";
/**
 * Why this act's verb is dead, in one line. The act's own sentence when it has
 * somewhere else to go, the generic one otherwise.
 */
export function jobActElsewhere(act) {
    const copy = jobActCopy(act);
    return copy?.elsewhere ?? JOB_ACT_NOT_ANSWERABLE_HERE;
}
/* The three acts that already had copy on the case file keep it word for word,
   so the cutover changes what the sentence is DERIVED from and not what a
   person reads. `work/workCopy.ts` re-exports these rather than restating
   them. */
export const ACCEPT_PROPOSAL_ACT_VERB = "Start plan";
export const ACCEPT_PROPOSAL_ACT_EFFECT = "Starting the plan hands this to the Team now, and its plan comes back here for your sign-off.";
const ACCEPT_PROPOSAL_ACT_HANDED_OFF = "Handed off, the team is drafting the plan. It comes back here for your sign-off.";
/**
 * What follows a plan on an Autonomous Plan line: nobody signs it off, Reeve
 * approves it himself and the build starts. Every sentence that promises "your
 * sign-off" reads this instead when the Team's Plan line is Autonomous.
 */
export const PLAN_AUTONOMOUS_FOLLOWS = "Reeve drafts the plan and approves it on its own, and the build follows.";
/** Start plan's effect, by the Plan line's rung; an unknown rung keeps the sign-off words. */
export function acceptProposalEffect(planAuthority) {
    return planAuthority === "autonomous"
        ? `Starting the plan hands this to the Team now. ${PLAN_AUTONOMOUS_FOLLOWS}`
        : ACCEPT_PROPOSAL_ACT_EFFECT;
}
/** What an accepted proposal reads once Start plan lands, by the Plan line's rung. */
export function acceptProposalHandedOff(planAuthority) {
    return planAuthority === "autonomous"
        ? `Handed off. ${PLAN_AUTONOMOUS_FOLLOWS}`
        : ACCEPT_PROPOSAL_ACT_HANDED_OFF;
}
export const PLAN_APPROVE_ACT_EFFECT = "Approving starts execution on this revision of the plan. Nothing comes back for approval unless the work goes off-plan.";
export const PLAN_REVISE_ACT_VERB = "Send back for another pass";
/**
 * Every act's copy. Total over `JobActId` by construction: an act added to the
 * closed list in `jobs.ts` without a row here is a type error, which is the
 * point. `hold` and `release` are reserved and nothing dispatches them yet;
 * they carry copy anyway so the day one does, no surface has to grow a branch.
 */
export const JOB_ACT_COPY = {
    accept_proposal: {
        verb: ACCEPT_PROPOSAL_ACT_VERB,
        effect: ACCEPT_PROPOSAL_ACT_EFFECT,
        declineLabel: "Doesn't need work",
        handedOff: ACCEPT_PROPOSAL_ACT_HANDED_OFF,
        stamp: "ACCEPTED",
        waitingPhrase: "accept {agent}'s proposal",
    },
    proceed_to_build: {
        verb: "Approve plan",
        effect: PLAN_APPROVE_ACT_EFFECT,
        declineLabel: PLAN_REVISE_ACT_VERB,
        declineEffect: "Sending it back produces the next revision of the plan and re-arms this gate. Nothing runs in the meantime.",
        feedbackPlaceholder: "What should the next pass change?",
        handedOff: "Executing now. The Team picks this up from here.",
        stamp: "APPROVED",
        waitingPhrase: "approve {agent}'s plan",
    },
    // The build line's act. Nobody is asked about it any more: the pull request
    // opens as a draft and Proctor's approval takes it out of draft, so this
    // copy only ever names the act on the record.
    open_pr: {
        verb: "Open the pull request",
        effect: "The pull request opens as a draft, and Proctor's approval takes it out of draft.",
        declineLabel: "Send back",
        handedOff: "The pull request is open as a draft.",
        stamp: "OPENED",
        waitingPhrase: "open {agent}'s pull request",
    },
    merge: {
        verb: "Merge PR",
        effect: "Merging lands this branch on the base branch through the GitHub app.",
        declineLabel: "Not yet",
        handedOff: "Merged.",
        stamp: "MERGED",
        waitingPhrase: "merge {agent}'s pull request",
        elsewhere: "This cannot be merged here yet. Merge it on GitHub.",
    },
    push_fix: {
        verb: "Push the fix",
        effect: "Pushing sends the fix to the branch and asks for another review round.",
        declineLabel: "Not yet",
        handedOff: "Pushed. The next review round runs on it.",
        stamp: "PUSHED",
        waitingPhrase: "let {agent} push the fix",
        elsewhere: "This cannot be pushed here yet. Its Agent pushes the fix on its next round.",
        capped: {
            verb: "One more round",
            effect: "One more round sends the open findings back to {agent}. It runs on what the ticket's budget has left, so give the ticket more in Team settings first. It grants exactly one, and the next review comes back here.",
            handedOff: "Pushing one more round. It comes back when the review posts.",
            stamp: "PUSHED",
            alternative: {
                choice: "take_over",
                label: "Take it over",
                effect: "Taking it over leaves the ticket in review for you to finish. {agent} pushes nothing more on it and the record says you took it.",
                handedOff: "Yours now. It stays in review with your name on it.",
                stamp: "TAKEN OVER",
            },
            declineChoice: "stop",
            declineLabel: "Stop here",
            declineEffect: "Stopping parks the ticket with your note. The pull request stays open and {agent} pushes nothing more.",
            declineHandedOff: "Stopped. The pull request stays open.",
            declineStamp: "STOPPED",
            feedbackPlaceholder: "Why it stops here…",
        },
        variants: {
            patch_ready: {
                verb: "Push the fix",
                effect: "Pushing sends this exact change to the pull request as one update, and the next review round runs on it.",
                handedOff: "Pushed to the pull request. The next review round runs on it.",
                stamp: "PUSHED",
                alternative: {
                    choice: "take_over",
                    label: "Take it over",
                    effect: "Taking it over leaves the ticket in review for you to finish. {agent} pushes nothing more on it and the record says you took it.",
                    handedOff: "Yours now. It stays in review with your name on it.",
                    stamp: "TAKEN OVER",
                },
                declineChoice: "stop",
                declineLabel: "Stop here",
                declineEffect: "Stopping parks the ticket with your note. The pull request stays open and {agent} pushes nothing more.",
                declineHandedOff: "Stopped. The pull request stays open.",
                declineStamp: "STOPPED",
                feedbackPlaceholder: "Why it stops here…",
            },
            // Wright stopped short of a push on its pull request and says why: a
            // stuck or unreadable check, a failure it cannot fix, the same failure
            // twice, a base that keeps conflicting, or someone else's push.
            pr_needs_person: {
                verb: "Keep going",
                effect: "{agent} reads the pull request again, waits once more for its checks and reviews, and fixes what it can in one push. If the same thing is still wrong, it asks again.",
                handedOff: "{agent} is reading the pull request again.",
                stamp: "CONTINUED",
                alternative: {
                    choice: "take_over",
                    label: "Take it over",
                    effect: "Taking it over leaves the ticket in review for you to finish. {agent} pushes nothing more on it and the record says you took it.",
                    handedOff: "Yours now. It stays in review with your name on it.",
                    stamp: "TAKEN OVER",
                },
                declineChoice: "stop",
                declineLabel: "Stop here",
                declineEffect: "Stopping parks the ticket with your note. The pull request stays open and {agent} pushes nothing more.",
                declineHandedOff: "Stopped. The pull request stays open.",
                declineStamp: "STOPPED",
                feedbackPlaceholder: "Why it stops here…",
            },
            round_stopped: {
                verb: "Try the round again",
                effect: "Trying again runs one more round with the same findings, and your note if you leave one, on the pull request as it is now. It counts as a round.",
                handedOff: "Running the round again.",
                stamp: "RETRIED",
                alternative: {
                    choice: "take_over",
                    label: "Take it over",
                    effect: "Taking it over leaves the ticket in review for you to finish. {agent} pushes nothing more on it and the record says you took it.",
                    handedOff: "Yours now. It stays in review with your name on it.",
                    stamp: "TAKEN OVER",
                },
                declineChoice: "stop",
                declineLabel: "Stop here",
                declineEffect: "Stopping parks the ticket with your note. The pull request stays open and {agent} pushes nothing more.",
                declineHandedOff: "Stopped. The pull request stays open.",
                declineStamp: "STOPPED",
                feedbackPlaceholder: "Why it stops here…",
                retryNotePlaceholder: "Anything the next round should know? (optional)",
            },
        },
    },
    approve_pr: {
        verb: "Accept result",
        effect: "Accepting marks this work done and on the record.",
        declineLabel: "Request changes",
        feedbackPlaceholder: "Notes for changes…",
        handedOff: "Done and on the record.",
        stamp: "POSTED",
        waitingPhrase: "accept {agent}'s result",
    },
    publish_verdict: {
        verb: "Publish the verdict",
        effect: "Publishing posts the evidence and the verdict on the pull request.",
        declineLabel: "Not yet",
        feedbackPlaceholder: "What should the next pass look at?",
        handedOff: "Published. The verdict and its evidence are on the pull request.",
        stamp: "PUBLISHED",
        waitingPhrase: "publish {agent}'s verdict",
        elsewhere: "This verdict cannot be published here yet. Fletcher publishes it when the line runs at autonomous.",
    },
    publish_sign_off: {
        verb: "Publish the sign-off",
        effect: "Publishing puts the sign-off and its evidence on the record.",
        declineLabel: "Not yet",
        handedOff: "Published. The sign-off is on the record.",
        stamp: "PUBLISHED",
        waitingPhrase: "publish {agent}'s sign-off",
        elsewhere: "This sign-off cannot be published here yet. Fletcher publishes it when the line runs at autonomous.",
    },
    send_back: {
        verb: "Send it back",
        effect: "Sending it back starts the next pass with this read attached.",
        declineLabel: "Leave it",
        feedbackPlaceholder: "What should the next pass change?",
        handedOff: "Sent back. The next pass runs with the read attached.",
        stamp: "SENT BACK",
        waitingPhrase: "send back what {agent} read",
        elsewhere: "This cannot be sent back here yet. Send the plan back from its own card above.",
    },
    request_changes: {
        verb: "Request the changes",
        effect: "Requesting changes posts the read as a changes-requested review.",
        declineLabel: "Leave it",
        feedbackPlaceholder: "What should change?",
        handedOff: "Requested. The changes are on the pull request.",
        stamp: "REQUESTED",
        waitingPhrase: "request the changes {agent} found",
        elsewhere: "These changes cannot be requested here yet. Request them on the pull request.",
    },
    route_question: {
        verb: "Post the question",
        effect: "Posting asks the named answerer in the Team's channel.",
        declineLabel: "Don't post",
        handedOff: "Posted. The answerer is tagged.",
        stamp: "POSTED",
        waitingPhrase: "post {agent}'s question",
        elsewhere: "This question cannot be posted here yet. Ask the answerer in the Team's channel.",
    },
    publish_brief: {
        verb: "Publish the Brief",
        effect: "Publishing posts this Brief to the Team's channel.",
        declineLabel: "Don't publish",
        handedOff: "Published.",
        stamp: "PUBLISHED",
        waitingPhrase: "publish {agent}'s Brief",
        elsewhere: "This Brief cannot be published here yet. Harper publishes it on its next run.",
    },
    post_work_log: {
        verb: "Post the work log",
        effect: "Posting sends the work log to the Team's delivery target.",
        declineLabel: "Don't post",
        handedOff: "Posted.",
        stamp: "POSTED",
        waitingPhrase: "post {agent}'s work log",
        elsewhere: "This cannot be posted here yet. Harper posts it on its next run.",
    },
    post_deploy_notes: {
        verb: "Post the notes",
        effect: "Posting sends the deploy notes to the Team's delivery target.",
        declineLabel: "Don't post",
        handedOff: "Posted.",
        stamp: "POSTED",
        waitingPhrase: "post {agent}'s deploy notes",
        elsewhere: "These notes cannot be posted here yet. Harper posts them on the next deploy.",
    },
    post: {
        verb: "Post it",
        effect: "Posting sends this to the Team's delivery target.",
        declineLabel: "Don't post",
        feedbackPlaceholder: "What should it say instead?",
        handedOff: "Posted.",
        stamp: "POSTED",
        waitingPhrase: "post what {agent} wrote",
        elsewhere: "This cannot be posted here yet. Its Agent posts it on its next run.",
    },
    "reviewer.claim_repo": {
        verb: "Review here instead",
        effect: "Moving the reviews brings this repository's pull requests to this Team. Open reviews stay with the Team that started them.",
        declineLabel: "Leave it where it is",
        handedOff: "Moved. This Team reviews it from the next pull request on.",
        stamp: "MOVED",
        waitingPhrase: "move this repository's reviews to {agent}",
        elsewhere: "A person moves reviews between Teams. The act is on the review line on the Team page.",
    },
    hold: {
        verb: "Hold it here",
        effect: "Holding keeps the ticket at this stage with the reason on the record.",
        declineLabel: "Let it move",
        handedOff: "Held, with the reason on the record.",
        stamp: "HELD",
        waitingPhrase: "hold the ticket {agent} is on",
        elsewhere: "This cannot be held here yet. Archive the ticket to keep it out of the queue.",
    },
    release: {
        verb: "Let it move",
        effect: "Releasing lets the ticket go on to the next stage.",
        declineLabel: "Keep holding",
        handedOff: "Released.",
        stamp: "RELEASED",
        waitingPhrase: "let {agent}'s ticket move",
        elsewhere: "This cannot be released here yet. Its Agent moves it on its next run.",
    },
};
/** The act's copy, or null when the gate carries no act. */
export function jobActCopy(act) {
    return act ? (JOB_ACT_COPY[act] ?? null) : null;
}
/**
 * Put the Agent's name where the copy left a slot.
 *
 * The fallback is "The Agent" rather than a bare drop, because every capped
 * sentence starts with the name ("Wright is out of rounds") and a sentence
 * that starts with a space is worse than a generic noun.
 */
function withAgent(text, agentName) {
    const agent = agentName?.trim();
    return text.replaceAll("{agent}", agent || "The Agent");
}
/**
 * The three-verb copy for an act that ran out of its cap, with the Agent's
 * name filled in. Null for every act that cannot run out, which is every act
 * but the fix round today.
 */
export function jobActCappedCopy(act, agentName) {
    const capped = jobActCopy(act)?.capped;
    if (!capped)
        return null;
    return {
        ...capped,
        verb: withAgent(capped.verb, agentName),
        effect: withAgent(capped.effect, agentName),
        handedOff: withAgent(capped.handedOff, agentName),
        alternative: {
            ...capped.alternative,
            label: withAgent(capped.alternative.label, agentName),
            effect: withAgent(capped.alternative.effect, agentName),
            handedOff: withAgent(capped.alternative.handedOff, agentName),
        },
        declineLabel: withAgent(capped.declineLabel, agentName),
        declineEffect: withAgent(capped.declineEffect, agentName),
        declineHandedOff: withAgent(capped.declineHandedOff, agentName),
    };
}
/**
 * Why a one-attempt lane plan gate on an Autonomous line waits (its payload's
 * `heldReason`): the plan spans repositories and nobody resolves to make the
 * split as. The held-plan release makes the split once somebody does
 * (`workItems/heldPlanRelease.ts`).
 */
export const SPLIT_NO_SUPERVISOR_HOLD = "split_no_supervisor";
const PLAN_HOLD_SENTENCES = new Map([
    ["plan_changes_requested", "Reeve's check asked for changes to this plan, so it waits for you."],
    // The one-attempt lane: an Autonomous line splits a plan that spans
    // repositories as the gate's supervisor, and this Team has none yet.
    [
        SPLIT_NO_SUPERVISOR_HOLD,
        "This plan spans repositories and the Team has no supervisor to split it as, so it waits for you.",
    ],
    [
        "no_human_execution_identity",
        "No person stands behind this build yet, so it waits for your approval.",
    ],
    // The act proceeded but the build could not start (the approval refused or
    // failed), so the gate is a person's again.
    ["build_refused", "The build could not start on its own, so it waits for your approval."],
    ["build_failed", "The build could not start on its own, so it waits for your approval."],
]);
const PLAN_EVIDENCE_HOLD_SENTENCE = "The plan names files or checks we could not confirm in the repository, so it waits for you.";
/**
 * Why a plan gate the plan act held is waiting, as one line for the
 * gate footer. Null for a reason it does not know, so a new hold reason shows
 * the ordinary gate rather than a wrong sentence.
 */
export function planHoldSentence(heldReason) {
    if (!heldReason)
        return null;
    if (heldReason.startsWith("evidence_"))
        return PLAN_EVIDENCE_HOLD_SENTENCE;
    return PLAN_HOLD_SENTENCES.get(heldReason) ?? null;
}
/**
 * The primary verb of a "needs a person" ask whose continue does something
 * other than keep going, by the ask's `evidence.askKind`. The other two
 * endings stay the variant's own.
 */
const PR_NEEDS_PERSON_PRIMARY = {
    review_incomplete: {
        verb: "Re-run review",
        effect: "Proctor reviews this push again from the start. The pull request stays a draft until a review finishes, and if this one cannot finish either, it asks again.",
        handedOff: "Proctor is reviewing the pull request again.",
        stamp: "RE-RUN",
    },
    // Nothing is left for Wright: the person approves (or merges) on GitHub.
    // The ask closes itself once GitHub shows the approval; checking again
    // reads the pull request now instead of at the next read.
    ready_for_person: {
        verb: "Check again",
        effect: "{agent} reads the pull request again. Once GitHub shows your approval, it moves on to the merge; anything new on it, {agent} works on first.",
        handedOff: "{agent} is reading the pull request again.",
        stamp: "CHECKED",
    },
};
/**
 * The three-ending copy for one of the act's named decisions, with the
 * Agent's name filled in. Null when the act has no such decision. `askKind`
 * is the evidence's own, read only by the "needs a person" decision.
 */
export function jobActVariantCopy(act, kind, agentName, askKind) {
    if (!isJobActVariantKind(kind))
        return null;
    const base = jobActCopy(act)?.variants?.[kind];
    if (!base)
        return null;
    const primary = kind === "pr_needs_person" && typeof askKind === "string"
        ? PR_NEEDS_PERSON_PRIMARY[askKind]
        : undefined;
    const variant = primary ? { ...base, ...primary } : base;
    return {
        ...variant,
        verb: withAgent(variant.verb, agentName),
        effect: withAgent(variant.effect, agentName),
        handedOff: withAgent(variant.handedOff, agentName),
        alternative: {
            ...variant.alternative,
            label: withAgent(variant.alternative.label, agentName),
            effect: withAgent(variant.alternative.effect, agentName),
            handedOff: withAgent(variant.alternative.handedOff, agentName),
        },
        declineLabel: withAgent(variant.declineLabel, agentName),
        declineEffect: withAgent(variant.declineEffect, agentName),
        declineHandedOff: withAgent(variant.declineHandedOff, agentName),
    };
}
/**
 * Which choices this act's click may carry. Empty for an act with one ending,
 * where a click says everything by being a click.
 */
export function jobActChoices(act) {
    const capped = jobActCopy(act)?.capped;
    if (!capped)
        return [];
    return ["continue", capped.alternative.choice, capped.declineChoice];
}
/**
 * The out-of-budget gate's own two lines, written once so the gate row, the
 * Slack card, the bell and the test all say the same thing. Fix rounds are
 * bounded by the ticket's one budget now (decision 6, 2026-10-19), not by a
 * count; the names stay so every reader keeps its import.
 */
export function outOfRoundsGateTitle(agentName, prNumber) {
    return `${withAgent("{agent}", agentName)} needs you: the ticket used its budget on pull request #${prNumber}`;
}
export function outOfRoundsGateBody(input) {
    const agent = withAgent("{agent}", input.agentName);
    const rounds = `${input.rounds} fix round${input.rounds === 1 ? "" : "s"}`;
    const findings = `${input.findings} finding${input.findings === 1 ? "" : "s"}`;
    return (`${agent} pushed ${rounds} and the review still has ${findings} open. ` +
        `Give the ticket more budget in Team settings for one more round, finish it yourself, or stop here with the pull request open.`);
}
/**
 * What the PR watcher's "needs a person" ask is about (`evidence.askKind`).
 * The one list: the watcher's titles and needs (`prSignals/sweep.ts`) and the
 * Work row's waiting phrase below are all keyed by it, so a new kind does not
 * compile until every one of them names it. An open ask of a kind no longer
 * on it is stale, and the integrity pass clears it (`retired_ask_kind`).
 */
export const PR_ASK_KINDS = [
    "foreign_push",
    "stuck_check",
    "unfixable_check",
    "unreadable_check",
    /** A commit status (a preview deploy, a coverage bot) did not pass; the status was read and is named. */
    "status_check",
    "repeated_failure",
    "conflict_churn",
    /** Proctor failed or gave up on this head: never green, a person re-runs it. */
    "review_incomplete",
    /**
     * Nothing is left for Wright on its newest push: a person approves on
     * GitHub (or merges, where no review is required). Opened by the sweep at
     * settle, never by the head's decision.
     */
    "ready_for_person",
    /** The round's launch was refused for a reason another try cannot change. */
    "round_refused",
    /** The safety net's: the head could not be read, or its round could not start, for too long. */
    "stalled",
];
/**
 * Which of the two customer classes each PR watcher ask is (`stopClass.ts`).
 * An `infra` kind never opens a gate: once its retries are spent it writes a
 * note on the pull request and the head settles. `review_incomplete` is the
 * one that depends on the pull request: it asks a person only when a human
 * reviewer is required there (see {@link prAskClassOf}); otherwise it is a
 * note too.
 */
export const PR_ASK_KIND_CLASS = {
    foreign_push: "infra",
    stuck_check: "infra",
    unfixable_check: "infra",
    unreadable_check: "infra",
    status_check: "infra",
    repeated_failure: "infra",
    conflict_churn: "infra",
    review_incomplete: "person",
    ready_for_person: "person",
    round_refused: "infra",
    stalled: "infra",
};
/**
 * The class of one ask on one pull request. `humanReviewRequired` is GitHub's
 * answer for the pull request: a review decision is reported only when the
 * branch requires an approving review.
 */
export function prAskClassOf(kind, facts = {}) {
    if (kind === "review_incomplete")
        return facts.humanReviewRequired === true ? "person" : "infra";
    return PR_ASK_KIND_CLASS[kind];
}
/**
 * What a "needs a person" ask is waiting for, off the gate's evidence. Every
 * such ask is a `push_fix` gate on the fix-round line, so the act alone would
 * print "let Wright push the fix" over a pull request Wright has finished.
 */
/** The ask kind of a stopped fix round's gate (`evidence.kind` `round_stopped`). */
const ROUND_STOPPED_ASK = "round_stopped";
/**
 * The ask a gate's evidence carries, or null when the gate is not a "needs a
 * person" ask. Fields of the wrong type read as absent, so a malformed payload
 * reads as the act's own phrase rather than a wrong one.
 */
export function jobActAskOf(evidence) {
    // A stopped round rides a push_fix gate too, and asks what happens next,
    // never for the push.
    if (evidence?.kind === "round_stopped")
        return { askKind: ROUND_STOPPED_ASK };
    if (!evidence || evidence.kind !== "pr_needs_person")
        return null;
    return {
        askKind: typeof evidence.askKind === "string" ? evidence.askKind : null,
        reviewDecision: typeof evidence.reviewDecision === "string" ? evidence.reviewDecision : null,
        afterApproval: evidence.afterApproval === true,
        draft: evidence.draft === true,
    };
}
/**
 * The waiting phrase of each "needs a person" ask but the "ready" one, which
 * reads GitHub's review decision. Exhaustive over {@link PrAskKind}.
 */
const PR_NEEDS_PERSON_WAITING = {
    foreign_push: "say whether {agent} keeps fixing after someone else's push",
    stuck_check: "re-run a stuck check on {agent}'s pull request",
    unfixable_check: "fix a failing check {agent} cannot fix",
    unreadable_check: "look at a failing check YAGNI cannot read",
    status_check: "fix a failing status on {agent}'s pull request",
    repeated_failure: "decide on checks that failed again after a fix round",
    conflict_churn: "decide on the base branch that keeps conflicting",
    review_incomplete: "re-run the review on {agent}'s pull request",
    round_refused: "unblock {agent}'s fix round",
    stalled: "look at {agent}'s pull request",
};
/**
 * A "ready" ask that GitHub still wants a person's approval on before the
 * merge. Same rule as the ask's own title ("Approve on GitHub"); a draft's
 * step is to mark it ready for review first.
 */
export function readyAskNeedsApproval(ask) {
    return (ask?.askKind === "ready_for_person" &&
        ask.afterApproval !== true &&
        ask.draft !== true &&
        (ask.reviewDecision === "REVIEW_REQUIRED" || ask.reviewDecision === "CHANGES_REQUESTED"));
}
/**
 * A "ready" ask: nothing is left for the Agent. GitHub still wants a person's
 * approval, or only the merge is left. Mirrors the ask's own title.
 */
function readyAskWaitingPhrase(ask) {
    if (ask.draft === true)
        return "mark {agent}'s pull request ready for review";
    if (readyAskNeedsApproval(ask))
        return "approve {agent}'s pull request on GitHub";
    return ask.afterApproval === true
        ? "merge {agent}'s pull request on GitHub"
        : "merge {agent}'s pull request";
}
/** The ask's own phrase, or null when it is not a "needs a person" ask this copy knows. */
function askWaitingPhrase(ask) {
    const kind = ask?.askKind;
    if (!kind)
        return null;
    if (kind === "ready_for_person")
        return readyAskWaitingPhrase(ask);
    if (kind === ROUND_STOPPED_ASK)
        return "decide on {agent}'s stopped fix round";
    // The kind arrives as a string off the wire; one this build does not know
    // keeps the act's phrase.
    return Object.hasOwn(PR_NEEDS_PERSON_WAITING, kind)
        ? PR_NEEDS_PERSON_WAITING[kind]
        : null;
}
/**
 * The act in a waiting sentence, with the Agent's name filled in. Falls back
 * to the Agent's own name alone when the act is unknown, because "waiting on
 * Priya" with no verb is still true. A `push_fix` gate that carries a "needs a
 * person" ask says what the ask needs, not the act it rides on.
 *
 * The gate's Job kind may say more than its act: a ticket a person assigned
 * to yagni (`pm.take_assigned`) is theirs, not the Agent's proposal, so it
 * reads "accept what Andres assigned" ("accept what was assigned" when the
 * assigner's name is unknown).
 */
export function jobActWaitingPhrase(act, agentName, ask, line) {
    const copy = jobActCopy(act);
    if (!copy)
        return null;
    if (line?.kind === "pm.take_assigned" && act === "accept_proposal") {
        const first = line.assignedByName?.trim().split(/\s+/)[0];
        return first ? `accept what ${first} assigned` : "accept what was assigned";
    }
    const phrase = (act === "push_fix" ? askWaitingPhrase(ask) : null) ?? copy.waitingPhrase;
    const agent = agentName?.trim();
    if (!agent) {
        // No Agent name to possess. Drop the possessive rather than print "'s".
        return phrase.replace("{agent}'s ", "the ").replace("{agent}", "the Team");
    }
    return phrase.replace("{agent}", agent);
}
/**
 * The Work row's sentence when a person is waiting: "Waiting on Priya to
 * accept Bailey's proposal".
 *
 * The supervisor's name is the addressee; "you" is used only when the reader
 * IS the supervisor, which the caller decides because only it knows who is
 * reading.
 */
export function jobWaitingSentence(input) {
    const phrase = jobActWaitingPhrase(input.act, input.agentName, null, {
        kind: input.kind,
        assignedByName: input.assignedByName,
    });
    if (!phrase)
        return null;
    const who = input.readerIsSupervisor ? "you" : input.supervisorName?.trim() || null;
    return who ? `Waiting on ${who} to ${phrase}` : `Waiting on a person to ${phrase}`;
}
/** "supervised by Priya", the Authority word on the right of the gate card. */
export function supervisedByLine(supervisorName) {
    const who = supervisorName?.trim();
    return who ? `supervised by ${who}` : null;
}
/**
 * Whose click it is, and that it is not only theirs (spec edge case 22: the
 * gate stays addressed to the supervisor, any member may answer, the record
 * names who did).
 */
export function whoseClickLine(supervisorName) {
    const who = supervisorName?.trim();
    return who
        ? `${who}'s click. Anyone on the Team can answer it, and the record names who did.`
        : "Anyone on the Team can answer this, and the record names who did.";
}
/** Why the buttons are disabled for a reader who is not on the Team. */
export const GATE_NOT_A_MEMBER = "You can read and comment here. Answering this is for the Team's members.";
/**
 * The short name of a kind's line, for places too narrow for the sentence: a
 * run row beside its cost, a spend breakdown under an Agent.
 *
 * A NAMED instance beats it, because a person who named a line named it to be
 * read ("Proctor · infra review"). The `default` instance has no name, so the
 * kind's own short word stands in.
 */
export const JOB_KIND_SHORT_LABEL = {
    "pm.propose": "proposals",
    "pm.take_assigned": "assigned tickets",
    "pm.route_question": "routed questions",
    "staff.plan": "planning",
    "builder.build": "builds",
    "builder.fix_round": "fix rounds",
    "builder.merge": "merges",
    "reviewer.review_pr": "PR reviews",
    "qa.verify_pr": "PR verification",
    "qa.sign_off_release": "release sign-off",
    "chronicler.brief": "the Brief",
    "chronicler.work_log": "the work log",
    "chronicler.deploy_notes": "deploy notes",
    "chronicler.custom": "standing work",
};
/**
 * "Proctor · infra review": the Agent and which of its lines. The one label
 * the runs list, the journey, and the Usage breakdown all print, so a person
 * reading spend and a person reading a run row are reading the same line.
 */
export function jobLineLabel(input) {
    const agent = input.agentName?.trim();
    const instance = input.instance?.trim();
    const named = instance && instance !== DEFAULT_JOB_INSTANCE ? instance : null;
    const kindLabel = input.kind && input.kind in JOB_KIND_SHORT_LABEL
        ? JOB_KIND_SHORT_LABEL[input.kind]
        : null;
    const what = named ?? kindLabel;
    if (agent && what)
        return `${agent} · ${what}`;
    return agent ?? what ?? null;
}
//# sourceMappingURL=jobGate.js.map